import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { Route as RouteIcon, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const CANCELLED_STATES = ['CANCELLED_FREE', 'CANCELLED_WITH_COSTS'];

const statusStyle = (status) => {
  if (CANCELLED_STATES.includes(status)) return 'bg-red-100 text-red-700 border border-red-300';
  switch (status) {
    case 'COMPLETED': return 'bg-emerald-100 text-emerald-700';
    case 'CONFIRMED': return 'bg-sky-100 text-sky-700';
    case 'PLANNED': return 'bg-indigo-100 text-indigo-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

// pickupTime.localTime is the journey's local time: format WITHOUT timezone conversion
const formatLocalTime = (localTime) => {
  if (!localTime) return '—';
  const m = String(localTime).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
  return localTime;
};

const formatUpdated = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function TransferzJourneys() {
  const { token } = useAuth();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resyncing, setResyncing] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchJourneys = useCallback(() => {
    return axios.get(`${API}/transferz/journeys`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setJourneys(r.data))
      .catch(() => setError('Impossibile caricare i viaggi Transferz'));
  }, [token]);

  useEffect(() => {
    fetchJourneys().finally(() => setLoading(false));
  }, [fetchJourneys]);

  const resync = async (jid) => {
    setResyncing(jid);
    try {
      await axios.post(`${API}/transferz/journeys/${jid}/resync`, {}, authHeaders);
      toast.success(`Viaggio ${jid} riallineato`);
      await fetchJourneys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Riallineamento fallito');
      await fetchJourneys();
    } finally {
      setResyncing(null);
    }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="transferz-journeys-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
          <RouteIcon className="h-5 w-5 text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Viaggi Transferz</h1>
          <p className="text-sm text-slate-500">Sola lettura — sincronizzati dai webhook, accesso riservato agli admin</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" data-testid="transferz-journeys-error">{error}</div>
      ) : journeys.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="transferz-journeys-empty">
          Nessun viaggio sincronizzato finora.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full" data-testid="transferz-journeys-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {['ID Viaggio', 'Codice', 'Stato', 'Assegnazione', 'Autista', 'Veicolo', 'Ritiro', 'Destinazione', 'Data ritiro', 'Tipo veicolo', 'Tariffa', 'Ultimo agg.', ''].map((h, i) => (
                  <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {journeys.map((j) => {
                const cancelled = CANCELLED_STATES.includes(j.status);
                return (
                  <tr key={j.journey_id} className={`border-b border-slate-100 ${cancelled ? 'bg-red-50' : 'hover:bg-slate-50'}`} data-testid={`transferz-journey-row-${j.journey_id}`}>
                    <td className="px-3 py-3 text-sm font-mono font-medium text-slate-800">
                      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-600 text-white mr-1.5">TEST</span>
                      {j.journey_id}
                    </td>
                    <td className="px-3 py-3 text-sm font-mono text-slate-600">{j.code ? `[TEST] ${j.code}` : '—'}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyle(j.status)}`} data-testid={`journey-status-${j.journey_id}`}>
                          {j.status || '—'}
                        </span>
                        {j.unassigned && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" data-testid={`journey-unassigned-${j.journey_id}`}>
                            Non più assegnato
                          </span>
                        )}
                        {j.needs_realign && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700" data-testid={`journey-realign-${j.journey_id}`}>
                            Da riallineare
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600">{j.assignment_status || '—'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{j.driver_present && j.driver_name ? j.driver_name : '—'}</td>
                    <td className="px-3 py-3 text-sm font-mono text-slate-600">{j.vehicle_present && j.vehicle_plate ? j.vehicle_plate : '—'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 max-w-[180px] truncate" title={j.pickup || ''}>{j.pickup || '—'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 max-w-[180px] truncate" title={j.dropoff || ''}>{j.dropoff || '—'}</td>
                    <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{formatLocalTime(j.pickup_time)}</td>
                    <td className="px-3 py-3 text-sm text-slate-600">{j.vehicle_category || '—'}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-800 whitespace-nowrap">
                      {j.fare?.includingVat != null ? `€${Number(j.fare.includingVat).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{formatUpdated(j.last_synced_at)}</td>
                    <td className="px-3 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resync(j.journey_id)}
                        disabled={resyncing === j.journey_id}
                        className="h-8 px-2 text-slate-600"
                        title="Riallinea"
                        data-testid={`resync-journey-${j.journey_id}`}
                      >
                        <RefreshCw className={`h-4 w-4 ${resyncing === j.journey_id ? 'animate-spin' : ''}`} />
                        <span className="ml-1 hidden xl:inline">Riallinea</span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
